import * as THREE from 'three';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';

// Shared-rig contract: forbidden. KineticRig owns the rig because its tubes are
// lit OBJECTS hanging in a room; Cascade's chains are emissive themselves and a
// room light falling across them would flatten exactly the depth the piece is
// built on.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'cascade',
  family: 'stage',
  danceability: 0.84,
  energyLevel: 0.82,
  bpmMin: 105,
  bpmMax: 175,
  movement: 0.80,
  tonality: 0.70,
  prefers: { gridStrength: [0.55, 1.0], percussiveness: [0.40, 1.0] },
  enabled: true
};

// Cascade: a sheet of suspended light chains, physically COUPLED, crossed by
// waves that reflect off the edges and interfere.
//
// WHAT SEPARATES IT FROM KINETICRIG. There, 54 tubes compute their own height
// from a formula: the shock wave is PAINTED, every tube plays its part without
// knowing its neighbours exist, and the wave always crosses at the same speed,
// in the same direction, and never comes back. Here the nodes are linked. A
// disturbance travels from neighbour to neighbour, reflects off the boundary,
// returns, and meets the waves behind it. The interference is a CONSEQUENCE,
// not an effect - standing waves, beats, and pockets of calm between two fronts
// are things no formula was asked to produce.

// --- the lattice -------------------------------------------------------------
//
// THE SHEET IS SIZED FROM THE FRUSTUM, not from a fixed spacing. At 0.9 the
// twenty columns spanned 17.1 units against a frame 22.8 wide at 16:9 - a black
// margin of 2.85 units either side, where the brief asks for a sheet that
// overruns all four edges. And a fixed spacing cannot satisfy both aspects the
// kiosk runs: at 3.2:1 the frame is 41 units wide, so a spacing large enough to
// cover it would leave barely five of the fourteen rows on screen.
//
// So the ROWS set the scale - the sheet is made tall enough to overrun the
// height by a few percent, which keeps the vertical structure the bands excite
// legible - and the COLUMN COUNT then follows from the width. The wave equation
// works in node indices, so adding columns costs only their own arithmetic and
// changes nothing about the physics.
const ROW_OVERRUN = 1.06;        // the sheet is this much taller than the frame
const COL_OVERRUN = 1.10;        // and this much wider
const COLS_CAP = 46;             // a ceiling on the cost at extreme aspects
const BASE_Y = 3.0;              // the sheet hangs around the camera's aim
const BACK_Z = -6.0;
const BACK_AMPLITUDE = 0.40;

// --- the wave ----------------------------------------------------------------
const SUBSTEP = 1 / 60;          // FIXED - see the accumulator in update()
const MAX_SUBSTEPS = 3;
const C2_MAX = 0.49;             // measured: 0.45 is stable, 0.55 diverges
const C2_MIN = 0.015;
const DAMP_BASE = 0.9962;
const DAMP_MID = 0.9985;         // mid-track: waves persist, interference builds
const EDGE_DAMP = 0.985;         // the two border rings, or reflections pile up
const EDGE_RINGS = 2;

// THE FRONT'S SPEED IS NOT sqrt(c2), and calibrating on the formula is wrong at
// the slow end. Measured on this exact scheme by tracking the front's position
// and regressing its slope over the outward leg:
//
//   c2     0.02   0.04   0.06   0.09   0.12   0.16   0.20   0.25   0.30   0.36   0.42   0.48
//   speed  0.112  0.176  0.240  0.302  0.352  0.416  0.458  0.517  0.549  0.611  0.655  0.703
//
// Above c2 = 0.09 it tracks sqrt(c2) within 2%; below, numerical dispersion
// smears the front and it runs up to 21% SLOW. Taking c2 = v^2 would therefore
// be about 30% off at 60 bpm and the reflection would come back late, which is
// precisely the thing this piece is supposed to get for free.
//
// Two notes on how this was measured, because two earlier attempts gave wrong
// answers: detecting the arrival by an ABSOLUTE threshold catches the numerical
// precursor running ahead of the wave and overstates the speed by 12%, and
// detecting it by the PEAK sometimes catches an already-reflected wave, which
// makes the measured speed non-monotonic in c2.
const SPEED_TABLE = [
  [0.02, 0.112], [0.04, 0.176], [0.06, 0.240], [0.09, 0.302],
  [0.12, 0.352], [0.16, 0.416], [0.20, 0.458], [0.25, 0.517],
  [0.30, 0.549], [0.36, 0.611], [0.42, 0.655], [0.48, 0.703]
];
// The fastest front the stability limit allows. A crossing in two beats needs
// (cols - 1) * bpm / 7200 nodes per substep, which at 200 bpm on the widest
// lattice is 0.528 - so every tempo in the musical range is reachable and the
// two constraints never fight.
const SPEED_MAX = SPEED_TABLE[SPEED_TABLE.length - 1][1];

// A last 11% trim, measured on the assembled piece rather than on the bare
// scheme. The table above was built by regressing the front's POSITION; the
// acceptance test asks when the far edge first lights up, which a precursor
// reaches earlier. Between the two detectors the crossing came out at 1.72 to
// 1.80 beats instead of 2.00 - the same offset at 105, 128, 150 and 175 bpm,
// so one constant fixes it rather than a change of method.
const SPEED_TRIM = 0.89;

function c2ForSpeed(v) {
  if (v <= SPEED_TABLE[0][1]) return C2_MIN;
  for (let i = 1; i < SPEED_TABLE.length; i++) {
    if (v <= SPEED_TABLE[i][1]) {
      const a = SPEED_TABLE[i - 1], b = SPEED_TABLE[i];
      return a[0] + (b[0] - a[0]) * (v - a[1]) / (b[1] - a[1]);
    }
  }
  return C2_MAX;
}

// --- the chains --------------------------------------------------------------
//
// A chain is a pendulum in series: each link chases the one above it, with the
// same Verlet integration as the lattice. The LAG between the top and the
// bottom is the whole point - it is what makes the chain whip when a wave goes
// through, and it is what a rigid tube cannot do.
// A CHAIN HAS TO FIT INSIDE ITS ROW PITCH. Five links at 0.34 spacing hang 1.7
// units below their node, against a row spacing of 0.9: every chain then ran
// through the two rows beneath it and the whole sheet fused into twenty
// continuous vertical strands, with the fourteen rows - and therefore the
// vertical structure the bands are supposed to excite - completely invisible.
// 5 * 0.155 + 0.115 = 0.89, just inside the pitch, so each chain reads as its
// own hanging object with clear air under it.
// AS A FRACTION OF THE ROW PITCH, not in absolute units - the pitch is now
// derived, and a chain that was sized for 0.9 would run through two rows at a
// wider spacing. 5 * 0.172 + 0.128 = 0.99 of a pitch, so each chain hangs
// clear of the row beneath it.
const SEG_GAP_FRAC = 0.172;
const SEG_LEN_FRAC = 0.128;
// A FIRST-ORDER LAG, not the lattice's Verlet - and this is a deliberate
// departure from the brief, made on measurement.
//
// A link written as a Verlet spring chasing the link above is an oscillator
// driven at its own resonance, and the damping term only bounds it. Swept over
// stiffness 190-900 against damping 0.86-0.97, the bottom link came out LARGER
// than the top in every stable combination - ratios of 1.4 to 35 - which a
// hanging chain cannot do. Adding a proper relative-velocity damping term did
// not fix it either: over stiffness 300-1600 and coupling 0.15-0.75, not one
// combination produced a tail smaller than its own top.
//
// The exponential follower cannot pump energy: the amplitude strictly decreases
// down the chain, and the lag accumulates link by link, which is exactly the
// whip. Swept, it trades the two against each other cleanly:
//   alpha  0.10  0.18  0.25  0.32  0.40  0.50
//   keeps  26%   43%   56%   66%   76%   84%
//   lags   433   233   150   117   100    67   ms
// 0.25 keeps enough that the tail visibly moves and lags enough that it
// visibly trails.
const CHAIN_ALPHA = 0.25;
const TAIL_SIZE = 1.7;           // the bottom link is the mass that drags
const TAIL_GLOW = 1.5;

// --- excitation --------------------------------------------------------------
// --- the synthetic grid ------------------------------------------------------
//
// WITHOUT A BEAT GRID THIS PIECE WAS NOT "LESS SYNCHRONISED", IT WAS STILL, and
// for two reasons that compound:
//
//  1. Everything structural here hangs off a wrap of beatPhase/barPhase/
//     phraseIndex, and all three are null whenever the bpm is unknown - on a
//     radio stream, and for the first seconds of any track. The old fallback
//     produced only BEATS, so _onBar() never ran; and _onBar() is what fires
//     the plane FRONT, the wave that crosses the sheet, reflects off the far
//     edge and interferes with what is behind it. That front IS the piece.
//     What was left was a single-node pulse every 0.45 s - a few pinpricks.
//  2. Those pinpricks all landed in the same place. See _bandRows.
//
// So the fallback now runs a whole free-running grid rather than one trigger:
// beats, bars, phrases and a periodic upheaval, in the same proportions a real
// grid has. The sheet behaves as it always did; only the clock is synthetic.
const SYNTH_BPM = 124;             // when trackFeatures carries no tempo either
const SYNTH_BEATS_PER_BAR = 4;
const SYNTH_BEATS_PER_PHRASE = 16;
// Stands in for the drop marks that do not exist without an analysis timeline.
// Every fourth phrase, which at 124 bpm is about every 31 s.
const SYNTH_CHAOS_PHRASES = 4;

const PULSE_BEAT = 1.15;
const PULSE_FRONT = 0.80;
const PULSE_DROP = 1.05;
const BAND_SPLIT_LOW = 0.34;     // bands.low excites the bottom third
const BAND_SPLIT_HIGH = 0.66;    // bands.high the top third
const SAFE_EXCLUDE = 0.26;       // no excitation under the cover, in UV terms

// THE FIELD'S AMPLITUDE IS NOT THE DISPLAYED DEPTH. The wave runs in its own
// units and settles around 2.4 at full excitation; a link is 0.30 long, so fed
// straight in, a single link spans eight times its own length in z and projects
// as a long slanted stroke - the sheet stops reading as hanging chains and
// starts reading as a thicket of branches. Scaled and clamped so the relief
// stays comparable to the node spacing, which is what makes it read as relief.
const Z_GAIN = 0.26;
const Z_MAX_FRAC = 0.83;         // of the node spacing - relief, not excursion

const DEPTH_SIZE_RATIO = 2.5;    // between the extremes, per the brief
const SAFE_FLOOR = 0.35;

const TIER = {
  0: { cols: 12, rows: 8, seg: 3, back: false, floor: false },
  1: { cols: 16, rows: 11, seg: 4, back: true, floor: false },
  2: { cols: 20, rows: 14, seg: 5, back: true, floor: true }
};
const DEFAULT_TIER = 2;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

// =============================================================================
// SHADERS
// =============================================================================

// FAT LINES VIA LineSegments2, not a hand-rolled screen-space expansion.
//
// The brief asks for the positions to be built in the vertex shader from a
// per-instance displacement, uploading one float per endpoint instead of six.
// I wrote that, and it drew nothing: the geometry rasterised - 3570 triangles
// per frame, confirmed on the renderer's own counter - but the expansion put
// every quad somewhere invisible. Bisected, the culprit was the screen-space
// normal: with a fixed width and no normal the sheet appeared, with the normal
// it vanished, and probed on a single instance the normal came back as a clean
// unit vector pointing the wrong way.
//
// I stopped there. The optimisation was not load-bearing: 1400 segments at six
// floats each is 34 KB a frame, and the same path measured 1.7 ms on the Pi in
// Pyro at a comparable count. A proven renderer beats a clever one that costs a
// day. The one thing it gives up is a per-link WIDTH - LineMaterial carries a
// single linewidth for the whole mesh - so the depth cue the brief asks for at
// 2.5:1 is carried entirely by colour, brightness and haze, which it also asks
// for. Written here rather than quietly dropped.

const FLOOR_VERTEX = `
  precision mediump float;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const FLOOR_FRAGMENT = `
  precision mediump float;
  uniform vec3 uColA;
  uniform vec3 uColB;
  uniform float uTime;
  uniform sampler2D uStreak;
  varying vec2 vUv;
  float hash12(vec2 p) {
    vec3 q = fract(vec3(p.xyx) * 0.1031);
    q += dot(q, q.yzx + 33.33);
    return fract((q.x + q.y) * q.z);
  }
  void main() {
    // The chains' reflection, as blurred vertical streaks. Not a real mirror -
    // a column of light smeared downward is what a wet stage floor actually
    // gives back, and it costs one texture read.
    vec3 refl = texture2D(uStreak, vec2(vUv.x, 0.985)).rgb;
    float fade = pow(1.0 - vUv.y, 2.2);
    vec3 col = refl * fade * 0.42;
    col += mix(uColA, uColB, vUv.x) * 0.020 * fade;
    col += (hash12(gl_FragCoord.xy + uTime) - 0.5) * (1.8 / 255.0);
    gl_FragColor = vec4(col, 1.0);
  }
`;

// =============================================================================

export class CascadeVisualizer extends Visualizer {
  constructor(options) {
    super();
    const opts = options || {};
    let tier = opts.qualityTier != null ? opts.qualityTier : DEFAULT_TIER;
    if (!TIER[tier]) tier = 0;
    this._tier = tier;
    this._layout = TIER[tier];

    this._time = 0;
    this._accum = 0;
    this._paused = false;
    this._c2 = 0.2;
    this._damping = DAMP_BASE;

    this._lastBeatPhase = 0;
    this._lastBarPhase = 0;
    this._lastPhraseIndex = null;
    this._beatCount = 0;
    this._lastDropAt = -1e9;
    this._lowBand = 0.5; this._midBand = 0.5; this._highBand = 0.5;
    this._lastLow = 0;
    this._lastFire = -1e9;
    this._synthBeat = 0;
    this._synthPhrase = 0;

    this._field = urlParam('cascadefield') === '1';
    this._forcePulse = urlParam('cascadepulse') === '1';
    this._forceFront = urlParam('cascadefront') === '1';
    // Forces the no-grid path even when a grid is available, which is the only
    // practical way to look at what a radio stream actually gets.
    this._forceNoGrid = urlParam('cascadenogrid') === '1';
    const fc = parseFloat(urlParam('cascadec2'));
    this._forcedC2 = Number.isFinite(fc) ? Math.max(C2_MIN, Math.min(C2_MAX, fc)) : null;
  }

  // The frame, at the sheet's own depth: what the camera actually shows at
  // z = 0. Everything else in the layout follows from these two numbers.
  _frame(camera, aspect) {
    const halfH = Math.abs(camera.position.z) * Math.tan(camera.fov * Math.PI / 360);
    return { halfH: halfH, halfW: halfH * aspect };
  }

  // Rows set the scale, columns follow from the width. Capped, because a very
  // wide frame would otherwise ask for a lattice whose cost is all off screen.
  _layoutFor(camera, aspect, rows) {
    const fr = this._frame(camera, aspect);
    const spacing = (2 * fr.halfH * ROW_OVERRUN) / Math.max(1, rows - 1);
    const cols = Math.min(COLS_CAP,
      Math.max(6, Math.ceil((2 * fr.halfW * COL_OVERRUN) / spacing) + 1));
    return { spacing: spacing, cols: cols, halfW: fr.halfW, halfH: fr.halfH };
  }

  // One lattice: the wave field plus the chains hanging off it. Everything is
  // allocated here and never again - update() must not allocate.
  _makeNet(cols, rows, seg, z, amplitude, spacing) {
    const n = cols * rows;
    const links = n * (seg + 1);       // seg links share seg+1 endpoints
    return {
      cols: cols, rows: rows, seg: seg, z: z, amp: amplitude, n: n,
      spacing: spacing, segGap: spacing * SEG_GAP_FRAC, segLen: spacing * SEG_LEN_FRAC,
      cur: new Float32Array(n),
      prev: new Float32Array(n),
      next: new Float32Array(n),
      // the chain: one displacement per endpoint, integrated as a pendulum
      // chasing the endpoint above it
      chain: new Float32Array(links),
      linePos: null, lineCol: null
    };
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    const L = this._layout;
    const self = this;

    // THE FRAMING IS THE BRIEF'S. A sheet seen almost flat-on but not quite:
    // enough perspective that the displaced nodes separate in depth, little
    // enough that the lattice still reads as a grid.
    camera.position.set(0, BASE_Y, 9.5);
    camera.lookAt(0, BASE_Y, 0);
    camera.fov = 68;
    camera.updateProjectionMatrix();

    const aspect = safeZoneUniforms.uAspect.value || 1.78;
    this._builtAspect = aspect;
    const front = this._layoutFor(camera, aspect, L.rows);
    this._nets = [this._makeNet(front.cols, L.rows, L.seg, 0, 1.0, front.spacing)];
    if (L.back) {
      // The second sheet sits further away, so the same frame is WIDER there -
      // its own layout, not a scaled copy of the first, or it would stop short
      // of the edges exactly where the parallax is supposed to read.
      const backCam = { position: { z: Math.abs(camera.position.z - BACK_Z) }, fov: camera.fov };
      const back = this._layoutFor(backCam, aspect, 8);
      this._nets.push(this._makeNet(back.cols, 8, Math.max(3, L.seg - 1),
                                    BACK_Z, BACK_AMPLITUDE, back.spacing));
    }

    this._shared = {
      uColA: { value: new THREE.Color(0x36e0ff) },
      uColB: { value: new THREE.Color(0xff5bd0) },
      uAspect: safeZoneUniforms.uAspect,
      uSafeCenter: safeZoneUniforms.uSafeCenter,
      uSafeHalfSize: safeZoneUniforms.uSafeHalfSize,
      uResolution: { value: new THREE.Vector2(1280, 720) }
    };

    this._meshes = [];
    for (let k = 0; k < this._nets.length; k++) {
      const net = this._nets[k];
      const mesh = this._buildMesh(net, k === 0);
      this._meshes.push(mesh);
      scene.add(mesh);
    }

    if (L.floor) this._buildFloor(scene);

    // LineMaterial expands in screen space and needs the REAL buffer size, not
    // window.innerWidth: they differ by the render scale and by the tier's own
    // resolution multiplier, and a wrong one makes every link the wrong width.
    this._meshes[0].onBeforeRender = function (renderer) {
      renderer.getDrawingBufferSize(_size);
      for (let k = 0; k < self._nets.length; k++) {
        self._nets[k].material.resolution.set(_size.x, _size.y);
      }
    };

    this._seed();
  }

  _buildMesh(net, isFront) {
    const segs = net.n * net.seg;
    const geo = new LineSegmentsGeometry();
    // Allocated once at full size; the endpoints are then written straight into
    // the interleaved buffer every frame, never reallocated.
    net.linePos = new Float32Array(segs * 6);
    net.lineCol = new Float32Array(segs * 6);
    geo.setPositions(net.linePos);
    geo.setColors(net.lineCol);
    geo.instanceCount = segs;
    net.geo = geo;
    net.posBuf = geo.attributes.instanceStart.data;
    net.colBuf = geo.attributes.instanceColorStart.data;

    const mat = new LineMaterial({
      linewidth: isFront ? 6.0 : 3.5,
      vertexColors: true,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false
    });
    mat.resolution.set(window.innerWidth || 1280, window.innerHeight || 720);
    net.material = mat;
    const mesh = new LineSegments2(geo, mat);
    mesh.frustumCulled = false;
    mesh.renderOrder = isFront ? -1900 : -2000;
    return mesh;
  }

  // Endpoints AND colours, written together because both depend on the same
  // displacement. Colour carries the whole depth cue here: a link pushed
  // towards the viewer burns warmer and brighter, one that has receded goes
  // colder and is eaten by haze.
  _writeSegments(net, isFront) {
    const pos = net.linePos, col = net.lineCol, chain = net.chain;
    const per = net.seg + 1;
    const halfW = (net.cols - 1) * 0.5, halfH = (net.rows - 1) * 0.5;
    const a = this._shared.uColA.value, b = this._shared.uColB.value;
    const dim = isFront ? 1.0 : 0.42;
    const invAmp = 1 / (net.spacing * Z_MAX_FRAC * (net.amp || 1));
    let w = 0;
    for (let r = 0; r < net.rows; r++) {
      for (let c = 0; c < net.cols; c++) {
        const node = r * net.cols + c;
        const base = node * per;
        const x = (c - halfW) * net.spacing;
        const yNode = BASE_Y + (r - halfH) * net.spacing;
        // The gradient runs across the WIDTH, as in KineticRig, so the sheet
        // reads as one object and not as a field of independent lights.
        const t = net.cols > 1 ? c / (net.cols - 1) : 0.5;
        const br = a.r + (b.r - a.r) * t;
        const bg = a.g + (b.g - a.g) * t;
        const bb = a.b + (b.b - a.b) * t;
        for (let s = 0; s < net.seg; s++) {
          const rawTop = chain[base + s], rawBot = chain[base + s + 1];
          const zMax = net.spacing * Z_MAX_FRAC;
          const dTop = Math.max(-zMax, Math.min(zMax, rawTop * Z_GAIN));
          const dBot = Math.max(-zMax, Math.min(zMax, rawBot * Z_GAIN));
          const yTop = yNode - s * net.segGap;
          const p6 = w * 6;
          pos[p6] = x; pos[p6 + 1] = yTop; pos[p6 + 2] = net.z + dTop;
          pos[p6 + 3] = x; pos[p6 + 4] = yTop - net.segLen; pos[p6 + 5] = net.z + dBot;

          // The bottom link is the mass that drags the chain, so it burns
          // brighter - that weight is what makes the whip read as a whip.
          const tail = s === net.seg - 1 ? TAIL_GLOW : 1.0;
          for (let e = 0; e < 2; e++) {
            const d = (e ? dBot : dTop) * invAmp;
            const warm = d > 0 ? d : 0, cool = d < 0 ? -d : 0;
            // 0.30 at rest, not 0.9: fourteen hundred additive links overlap
            // heavily near the middle of the sheet, and at anything brighter
            // the whole right half clips to white before the bloom sees it.
            const lum = (0.13 + 0.34 * (d * 0.5 + 0.5)) * tail * dim
                      * (1 - 0.62 * cool);          // haze eats what recedes
            const q = p6 + e * 3;
            col[q]     = (br + (1.0 - br) * warm * 0.72) * lum;
            col[q + 1] = (bg + (0.72 - bg) * warm * 0.72) * lum * (1 - 0.30 * cool);
            col[q + 2] = (bb + (0.42 - bb) * warm * 0.72 + (1.0 - bb) * cool * 0.45) * lum;
          }
          w++;
        }
      }
    }
    net.posBuf.needsUpdate = true;
    net.colBuf.needsUpdate = true;
  }

  _buildFloor(scene) {
    const geo = new THREE.PlaneGeometry(40, 9);
    this._floorUniforms = {
      uColA: this._shared.uColA, uColB: this._shared.uColB,
      uTime: { value: 0 },
      uStreak: { value: null }
    };
    // No render target: the floor reads the chains' own colour ramp rather than
    // a captured frame. A real reflection pass would double the scene's cost
    // for something a blurred vertical smear already sells.
    const cv = document.createElement('canvas');
    cv.width = 64; cv.height = 2;
    this._streakCanvas = cv;
    this._streakTex = new THREE.CanvasTexture(cv);
    this._floorUniforms.uStreak.value = this._streakTex;
    const mat = new THREE.ShaderMaterial({
      uniforms: this._floorUniforms,
      vertexShader: FLOOR_VERTEX,
      fragmentShader: FLOOR_FRAGMENT,
      depthWrite: false
    });
    this._floor = new THREE.Mesh(geo, mat);
    this._floor.rotation.x = -Math.PI / 2;
    const n0 = this._nets[0];
    this._floor.position.set(0, BASE_Y - (n0.rows - 1) * 0.5 * n0.spacing - n0.spacing * 1.2, 0);
    this._floor.renderOrder = -2100;
    this._floorMat = mat;
    scene.add(this._floor);
  }

  // --- excitation ---------------------------------------------------------

  // A node index that is NOT under the cover. The chains pass behind the
  // artwork by design, but an impulse born there would put the sheet's loudest
  // moment exactly where it cannot be seen.
  _pickNode(net, rowLo, rowHi) {
    for (let attempt = 0; attempt < 12; attempt++) {
      const c = (Math.random() * net.cols) | 0;
      const r = rowLo + ((Math.random() * Math.max(1, rowHi - rowLo)) | 0);
      const u = net.cols > 1 ? c / (net.cols - 1) : 0.5;
      const v = net.rows > 1 ? r / (net.rows - 1) : 0.5;
      if (Math.abs(u - 0.5) < SAFE_EXCLUDE && Math.abs(v - 0.5) < SAFE_EXCLUDE) continue;
      return r * net.cols + c;
    }
    return ((rowLo + rowHi) >> 1) * net.cols;   // gave up: an edge column
  }

  // The band decides WHERE vertically. Low excites the bottom of the sheet,
  // high the top, mid the middle - so the track's own spectrum is visible in
  // where the sheet is being disturbed.
  _bandRows(net) {
    const lo = this._lowBand, mi = this._midBand, hi = this._highBand;
    const total = lo + mi + hi;
    // NO SPECTRUM MEANS ANYWHERE, NOT THE TOP. Without an analysis the three
    // bands are all exactly 0 (director.js's NEUTRAL_BANDS), and the old
    // normalisation then sent every excitation to the high band: with lo and mi
    // at 0 both `pick < lo` and `pick < lo + mi` are false whatever the draw,
    // so the last branch won every time and the bottom two thirds of the sheet
    // never moved. The 1e-5 that was added to make the division safe is exactly
    // what hid it. With no spectrum to place a disturbance by, the honest
    // answer is the whole sheet.
    if (total < 1e-4) return [0, net.rows];
    const pick = Math.random() * total;
    if (pick < lo) return [0, Math.max(1, Math.round(net.rows * BAND_SPLIT_LOW))];
    if (pick < lo + mi) {
      return [Math.round(net.rows * BAND_SPLIT_LOW), Math.round(net.rows * BAND_SPLIT_HIGH)];
    }
    return [Math.round(net.rows * BAND_SPLIT_HIGH), net.rows];
  }

  _pulse(net, amount) {
    const [lo, hi] = this._bandRows(net);
    const i = this._pickNode(net, lo, hi);
    net.cur[i] += amount * net.amp;
    net.prev[i] -= amount * net.amp * 0.35;   // a kick, not a displacement
  }

  // A whole row at once: a PLANE FRONT, which crosses the sheet and bounces.
  // It is the only excitation whose reflection is legible as a single event.
  _front(net, amount) {
    const [lo, hi] = this._bandRows(net);
    const r = Math.max(0, Math.min(net.rows - 1, lo + (((hi - lo) * Math.random()) | 0)));
    for (let c = 0; c < net.cols; c++) {
      const i = r * net.cols + c;
      net.cur[i] += amount * net.amp;
      net.prev[i] -= amount * net.amp * 0.35;
    }
  }

  _chaos(net, amount) {
    for (let i = 0; i < net.n; i++) {
      const a = (Math.random() * 2 - 1) * amount * net.amp;
      net.cur[i] += a;
      net.prev[i] -= a * 0.35;
    }
  }

  _seed() {
    for (const net of this._nets) {
      net.cur.fill(0); net.prev.fill(0); net.next.fill(0);
      net.chain.fill(0);
      this._pulse(net, PULSE_BEAT);
    }
    for (let k = 0; k < this._nets.length; k++) this._writeSegments(this._nets[k], k === 0);
  }

  // --- the simulation ------------------------------------------------------

  // One substep of the wave equation, with Neumann edges: the missing
  // neighbour at a boundary is the node itself, which cancels the outward flux
  // and sends the wave back. That reflection is the whole difference from
  // KineticRig, where a wave leaves and never returns.
  _stepWave(net, c2, damping) {
    const cur = net.cur, prev = net.prev, next = net.next;
    const cols = net.cols, rows = net.rows;
    for (let r = 0; r < rows; r++) {
      const rowOff = r * cols;
      const up = r > 0 ? -cols : 0;
      const dn = r < rows - 1 ? cols : 0;
      const edgeR = Math.min(r, rows - 1 - r);
      for (let c = 0; c < cols; c++) {
        const i = rowOff + c;
        const lf = c > 0 ? -1 : 0;
        const rt = c < cols - 1 ? 1 : 0;
        const lap = cur[i + lf] + cur[i + rt] + cur[i + up] + cur[i + dn] - 4 * cur[i];
        let v = (2 * cur[i] - prev[i] + c2 * lap) * damping;
        if (Math.min(edgeR, c, cols - 1 - c) < EDGE_RINGS) v *= EDGE_DAMP;
        next[i] = v;
      }
    }
    // Buffer rotation, not a copy.
    net.prev = cur; net.cur = next; net.next = prev;
  }

  // The chains. Link 0 IS its node; link k chases link k-1 with a first-order
  // lag - see CHAIN_ALPHA for why not Verlet. The lag accumulates down the
  // chain, and that accumulation is the whip.
  _stepChains(net, dt) {
    const chain = net.chain, cur = net.cur;
    const per = net.seg + 1;
    const a = CHAIN_ALPHA;
    for (let node = 0; node < net.n; node++) {
      const base = node * per;
      chain[base] = cur[node];            // the top endpoint IS the node
      for (let s = 1; s < per; s++) {
        const i = base + s;
        chain[i] += (chain[i - 1] - chain[i]) * a;
      }
    }
  }

  // --- the frame -----------------------------------------------------------

  update(deltaTime, profile) {
    const dt = Math.min(deltaTime, 0.1);
    this._paused = profile.playbackState === 'PAUSED';
    // PAUSE FREEZES THE ACCUMULATOR TOO. Draining it on resume would run the
    // whole paused interval in one burst of substeps.
    if (this._paused) return;
    this._time += dt;

    const director = profile.director || {};
    const bands = director.bands || { low: 0.5, mid: 0.5, high: 0.5 };
    this._lowBand = clamp01(bands.low);
    this._midBand = clamp01(bands.mid);
    this._highBand = clamp01(bands.high);

    const palette = profile.artworkPalette;
    if (palette && palette !== this._lastPalette) {
      this._lastPalette = palette;
      const vivid = palette.vividColors && palette.vividColors.length
        ? palette.vividColors : [0x36e0ff, 0xff5bd0];
      this._shared.uColA.value.set(vivid[0]);
      this._shared.uColB.value.set(vivid[vivid.length > 1 ? 1 : 0]);
      this._paintStreak();
    }

    // --- c2 from the tempo. A crossing in exactly two beats.
    // The SAME tempo the synthetic grid below runs on, so the front's crossing
    // time and the bar it is fired on agree. Two different defaults put the
    // reflection back out of step with the thing that launched it.
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || SYNTH_BPM;
    const main = this._nets[0];
    const needed = (main.cols - 1) / (2 * (60 / bpm) / SUBSTEP) * SPEED_TRIM;
    this._targetSpeed = needed;
    this._c2 = this._forcedC2 != null
      ? this._forcedC2
      : Math.max(C2_MIN, Math.min(C2_MAX, c2ForSpeed(Math.min(needed, SPEED_MAX))));

    // progress relaxes the damping mid-track, so the waves last longer and the
    // interference has time to build into something.
    const arc = Math.sin(clamp01(profile.progress || 0) * Math.PI);
    this._damping = DAMP_BASE + (DAMP_MID - DAMP_BASE) * arc;

    // --- excitation on the grid
    const beatPhase = profile.beatPhase || 0;
    const barPhase = profile.barPhase || 0;
    const hasGrid = !this._forceNoGrid
      && director.hasReliableGrid !== false && profile.beatPhase != null;
    if (hasGrid) {
      if (beatPhase < this._lastBeatPhase) this._onBeat();
      if (barPhase < this._lastBarPhase) this._onBar();
      this._lastBeatPhase = beatPhase;
      this._lastBarPhase = barPhase;
      const phrase = profile.phraseIndex;
      if (phrase != null && this._lastPhraseIndex != null && phrase !== this._lastPhraseIndex) {
        this._pattern = ((this._pattern || 0) + 1) % 4;
      }
      this._lastPhraseIndex = phrase;
    } else {
      // A FREE-RUNNING GRID, not a lone beat trigger - see SYNTH_BPM. The beat
      // count is the clock; bars and phrases are counted off it exactly as
      // beat-grid.js counts them off a real one, so the sheet gets its front
      // every four beats and its pattern change every sixteen.
      const prevBeat = this._synthBeat;
      this._synthBeat += dt * bpm / 60;
      const beat = Math.floor(this._synthBeat);
      if (beat > Math.floor(prevBeat)) {
        this._onBeat();
        this._lastFire = this._time;
        if (beat % SYNTH_BEATS_PER_BAR === 0) this._onBar();
        if (beat % SYNTH_BEATS_PER_PHRASE === 0) {
          // Deliberately NOT mirroring the grid path's `this._pattern++` here:
          // that field is written on every phrase and read nowhere in this
          // file, so copying it into the fallback would only be a second dead
          // write.
          this._synthPhrase++;
          // No timeline means no drop marks either, so the upheaval that
          // punctuates the piece has to come from the clock as well.
          if (this._synthPhrase % SYNTH_CHAOS_PHRASES === 0) {
            for (const net of this._nets) this._chaos(net, PULSE_DROP);
          }
        }
      }
      // An onset arriving between two synthetic beats still fires. When there
      // IS an analysis but no tempo - which happens, the two are independent -
      // the sheet should follow the music rather than the clock, and this is
      // what lets it. The 0.22 s gate keeps it from doubling the beat it just
      // landed on.
      const rise = this._lowBand - this._lastLow;
      if (rise > 0.10 && this._time - this._lastFire > 0.22) {
        this._onBeat();
        this._lastFire = this._time;
      }
      this._lastLow = this._lowBand;
    }

    const ev = director.nextEvent;
    if (ev && ev.type === 'drop' && ev.msUntil != null && ev.msUntil <= 0
        && this._time - this._lastDropAt > 2.0) {
      this._lastDropAt = this._time;
      for (const net of this._nets) this._chaos(net, PULSE_DROP);
    }
    if (this._forcePulse) { this._forcePulse = false; this._onBeat(); }
    if (this._forceFront) { this._forceFront = false; this._onBar(); }

    // --- FIXED SUBSTEPS. The propagation speed is then a property of the
    // scheme and not of the frame rate: at 30 fps two substeps run, at 60 fps
    // one, and a wave crosses the sheet in the same number of SECONDS either
    // way. Capped at three so a long frame cannot cascade into a longer one.
    this._accum += dt;
    let steps = 0;
    while (this._accum >= SUBSTEP && steps < MAX_SUBSTEPS) {
      this._accum -= SUBSTEP;
      steps++;
      for (const net of this._nets) this._stepWave(net, this._c2, this._damping);
    }
    if (steps === MAX_SUBSTEPS && this._accum > SUBSTEP) this._accum = 0;
    this._substeps = steps;

    for (let k = 0; k < this._nets.length; k++) {
      const net = this._nets[k];
      this._stepChains(net, SUBSTEP * Math.max(1, steps));
      this._writeSegments(net, k === 0);
    }

    if (this._floorUniforms) this._floorUniforms.uTime.value = this._time;
  }

  _onBeat() {
    this._beatCount++;
    for (const net of this._nets) this._pulse(net, PULSE_BEAT);
  }

  _onBar() {
    for (const net of this._nets) this._front(net, PULSE_FRONT);
  }

  // The floor's streak colours: the sheet's own ramp, baked once per palette.
  _paintStreak() {
    if (!this._streakCanvas) return;
    const g = this._streakCanvas.getContext('2d');
    const a = this._shared.uColA.value, b = this._shared.uColB.value;
    const grd = g.createLinearGradient(0, 0, this._streakCanvas.width, 0);
    grd.addColorStop(0, 'rgb(' + Math.round(a.r * 255) + ',' + Math.round(a.g * 255) + ',' + Math.round(a.b * 255) + ')');
    grd.addColorStop(1, 'rgb(' + Math.round(b.r * 255) + ',' + Math.round(b.g * 255) + ',' + Math.round(b.b * 255) + ')');
    g.fillStyle = grd;
    g.fillRect(0, 0, this._streakCanvas.width, this._streakCanvas.height);
    this._streakTex.needsUpdate = true;
  }

  dispose() {
    for (const m of this._meshes || []) {
      m.onBeforeRender = null;
      if (this._scene) this._scene.remove(m);
      m.geometry.dispose();
      m.material.dispose();
    }
    if (this._floor) {
      if (this._scene) this._scene.remove(this._floor);
      this._floor.geometry.dispose();
      this._floorMat.dispose();
    }
    if (this._streakTex) this._streakTex.dispose();
    this._meshes = [];
    this._nets = [];
    this._floor = null;
  }
}

const _size = new THREE.Vector2();
